/* eslint-disable max-lines */
// TODO: https://linear.app/clipboardhealth/issue/ACT-3976 -
// Once we remove hellosign code in cleanup, number of lines should be reduced

/* eslint-disable unicorn/prevent-abbreviations */
import { PageHeaderWrapper, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { FullScreenDialog, useToast } from "@src/appV2/lib";
import { addBrowserListener, openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useHelloSignEmbeddedFlow } from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/useHelloSignEmbeddedFlow";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { DocumentStatus } from "../../../../../Accounts/Documents/types";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "../../../../../lib/analytics";
import { PageHeaderTopRow } from "../../../../components/PageHeaderTopRow";
import { invalidateWorkerRequirements } from "../../../api/invalidateWorkerRequirements";
import {
  DOCUMENT_DETAILS_FULL_PATH,
  DOCUMENT_SUBMISSION_APPROVED_PATH,
  DOCUMENT_SUBMISSION_ERROR_PATH,
  DOCUMENT_SUBMISSION_IN_PROGRESS_PATH,
  DOCUMENT_SUBMISSION_IN_REVIEW_PATH,
} from "../../../paths";
import { DocumentSubmissionInProgressBottomSheet } from "../../../Submission/InProgressBottomSheet";
import { DocumentUploadFooter } from "../../Footer";
import { DocumentDetailsInstructions } from "../../Instructions";
import { DocumentDetailsMetadata } from "../../Metadata";
import { DocumentDetailsRequiredBySection } from "../../RequiredBySection/Section";
import { DocumentPageScrollWrapper } from "../../ScrollWrapper";
import { DocumentDetailsStatusCard } from "../../StatusCard";
import { useDocumentDetailsActions } from "../../useDocumentDetailsActions";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";
import { PandaDocFailureBottomSheet } from "../PandaDoc/PandaDocFailureBottomSheet";
import { PandaDocSigningContent } from "../PandaDoc/PandaDocSigningContent";
import { QuizNotPassedBottomSheet } from "../PandaDoc/QuizNotPassedBottomSheet/QuizNotPassedBottomSheet";
import { type ESignWorkflowSessionAttributes } from "../PandaDoc/types";
import { usePandaDocConfig } from "../PandaDoc/usePandaDocConfig";
import { usePandaDocSigningEnabled } from "../PandaDoc/usePandaDocSigningEnabled";
import { usePandaDocSigningSession } from "../PandaDoc/usePandaDocSigningSession";
import { useHelloSignFallbackUrl } from "./useHelloSignFallbackUrl";

const signType = "pandaDoc";

export function DocumentDetailsSignWithHelloSignPage() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const pandaDocModalState = useModalState();
  const pandaDocFailureModalState = useModalState();
  const submittingDocumentModalState = useModalState();
  const helloSignFormModalState = useModalState();
  const quizNotPassedModalState = useModalState();
  const [quizResultData, setQuizResultData] =
    useState<NonNullable<ESignWorkflowSessionAttributes["quizResult"]>>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const helloSignIframeContainerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showInfoToast } = useToast();
  const { redirectToSupport, useFallbackUrl } = usePandaDocConfig();

  const { status, requirementId, requirement } = workerRequirement;
  const { name } = requirement?.attributes ?? {};
  const pandaDocFallbackUrl = requirement?.attributes.pandaDocConfig?.fallbackUrl;
  const { deleteDocument } = useDocumentDetailsActions();
  const shouldUseHelloSignFallbackUrl = useHelloSignFallbackUrl();

  const isPandaDocSigningEnabled = usePandaDocSigningEnabled(requirementId);

  const {
    signableESignWorkflowSession,
    isLoadingPandaDoc,
    isLoadingSession,
    startSigning,
    handleDocumentLoaded,
    completeSigningAndGetDocumentStatus,
  } = usePandaDocSigningSession({
    requirementId,
    signType,
    onSessionLoadError: useCallback(
      (error: unknown) => {
        pandaDocModalState.closeModal();
        logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
          error,
          metadata: { requirementId },
        });
        pandaDocFailureModalState.openModal();
      },
      [pandaDocModalState, pandaDocFailureModalState, requirementId]
    ),
  });

  const onPandaDocCompleted = useCallback(async () => {
    pandaDocModalState.closeModal();
    submittingDocumentModalState.openModal();
    try {
      const { documentStatus, quizResult } = await completeSigningAndGetDocumentStatus();

      submittingDocumentModalState.closeModal();
      logEvent(APP_V2_USER_EVENTS.PANDADOC_SDK_DOCUMENT_SIGNED, {
        requirementId,
        signType,
      });

      if (documentStatus === DocumentStatus.REJECTED && isDefined(quizResult)) {
        setQuizResultData(quizResult);
        quizNotPassedModalState.openModal();
        return;
      }

      const modalPath =
        documentStatus === DocumentStatus.APPROVED
          ? DOCUMENT_SUBMISSION_APPROVED_PATH
          : DOCUMENT_SUBMISSION_IN_REVIEW_PATH;

      history.replace(
        generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${modalPath}`, {
          requirementId,
        })
      );
    } catch (error: unknown) {
      logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
        error,
        metadata: { requirementId },
      });
      submittingDocumentModalState.closeModal();
      pandaDocFailureModalState.openModal();
    }
  }, [
    history,
    submittingDocumentModalState,
    pandaDocFailureModalState,
    quizNotPassedModalState,
    requirementId,
    pandaDocModalState,
    completeSigningAndGetDocumentStatus,
  ]);

  const handleRetakeQuiz = useCallback(() => {
    quizNotPassedModalState.closeModal();
    setQuizResultData(undefined);
    pandaDocModalState.openModal();
    startSigning();
  }, [quizNotPassedModalState, pandaDocModalState, startSigning]);

  const handleCloseQuizNotPassedModal = useCallback(() => {
    quizNotPassedModalState.closeModal();
    setQuizResultData(undefined);
  }, [quizNotPassedModalState]);

  const { isCreatingHelloSignSession, isSubmittingHelloSignDocument, startHelloSignEmbeddedFlow } =
    useHelloSignEmbeddedFlow({
      requirementId,
      onSuccess: (isDocumentApproved) => {
        const modalPath = isDocumentApproved
          ? DOCUMENT_SUBMISSION_APPROVED_PATH
          : DOCUMENT_SUBMISSION_IN_REVIEW_PATH;

        void invalidateWorkerRequirements(queryClient);
        helloSignFormModalState.closeModal();
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${modalPath}`, {
            requirementId,
          })
        );
      },
      onError: () => {
        helloSignFormModalState.closeModal();
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
            requirementId,
          })
        );
      },
      helloSignIframeContainerRef,
      onHelloSignClientCancel: () => {
        helloSignFormModalState.closeModal();
      },
    });

  useEffect(() => {
    if (isSubmittingHelloSignDocument) {
      history.replace(
        generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_IN_PROGRESS_PATH}`, {
          requirementId,
        })
      );
    }
  }, [history, isSubmittingHelloSignDocument, requirementId]);

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={name ?? ""}
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        <DocumentDetailsMetadata />
        <DocumentDetailsStatusCard />
        <DocumentDetailsInstructions />
        <DocumentDetailsRequiredBySection />
      </DocumentPageScrollWrapper>

      <DocumentUploadFooter
        status={status}
        isLoading={isCreatingHelloSignSession || isLoadingSession}
        onSignButtonClick={async () => {
          if (isPandaDocSigningEnabled && useFallbackUrl && isDefined(pandaDocFallbackUrl)) {
            const browserFinishedListener = await addBrowserListener(
              "browserFinished",
              async () => {
                showInfoToast(
                  "If you have signed this document, please check back here later for an update"
                );
                await browserFinishedListener.remove();
              }
            );
            await openInBrowser({ url: pandaDocFallbackUrl }).catch(async () => {
              pandaDocFailureModalState.openModal();
              await browserFinishedListener.remove();
            });
            return;
          }

          if (redirectToSupport && isPandaDocSigningEnabled) {
            pandaDocFailureModalState.openModal();
            return;
          }

          if (isPandaDocSigningEnabled) {
            pandaDocModalState.openModal();
            startSigning();
            return;
          }

          if (
            isDefined(requirement?.attributes.helloSignConfig?.fallbackStaticUrl) &&
            (shouldUseHelloSignFallbackUrl ||
              !requirement.attributes.helloSignConfig.isEmbeddedFlowEnabled)
          ) {
            const browserFinishedListener = await addBrowserListener(
              "browserFinished",
              async () => {
                showInfoToast(
                  "If you have signed this document, please check back here later for an update"
                );
                await browserFinishedListener.remove();
              }
            );
            await openInBrowser({ url: requirement.attributes.helloSignConfig.fallbackStaticUrl });
          } else {
            helloSignFormModalState.openModal();
            void startHelloSignEmbeddedFlow();
          }
        }}
        onDeleteButtonClick={deleteDocument}
      />

      {/* Using skipTopSafeAreaPadding because we are using a custom header */}
      <FullScreenDialog skipTopSafeAreaPadding variant="tertiary" modalState={pandaDocModalState}>
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow
            title={name}
            onClickBack={() => {
              pandaDocModalState.closeModal();
              logEvent(APP_V2_USER_EVENTS.PANDADOC_SIGNING_CLOSED, {
                requirementId,
                signType,
              });
            }}
          />
        </PageHeaderWrapper>
        <PandaDocSigningContent
          isLoading={isLoadingPandaDoc}
          pandaDocSessionId={signableESignWorkflowSession?.attributes.esignProviderData?.sessionId}
          onDocumentLoaded={handleDocumentLoaded}
          onError={() => {
            pandaDocModalState.closeModal();
            logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
              error: new Error("PandaDoc Embedded Signing SDK Error"),
              metadata: { requirementId },
            });
            pandaDocFailureModalState.openModal();
          }}
          onComplete={onPandaDocCompleted}
        />
      </FullScreenDialog>
      <FullScreenDialog modalState={helloSignFormModalState}>
        <Box ref={helloSignIframeContainerRef} sx={{ height: "100%" }} />
      </FullScreenDialog>

      <DocumentSubmissionInProgressBottomSheet modalState={submittingDocumentModalState} />
      <PandaDocFailureBottomSheet modalState={pandaDocFailureModalState} />
      {isDefined(quizResultData) && (
        <QuizNotPassedBottomSheet
          modalState={quizNotPassedModalState}
          quizResult={quizResultData}
          onClose={handleCloseQuizNotPassedModal}
          onRetakeQuiz={handleRetakeQuiz}
        />
      )}
    </PageWrapper>
  );
}
/* eslint-enable max-lines */
/* eslint-enable unicorn/prevent-abbreviations */
